
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * MarkupValue struct
 */
class MarkupValue {
  /**
   * Initialize struct
   * @param {!UUID=} symbolId
   * @param {!number=} bid
   * @param {!number=} ask
   * @constructor
   */
  constructor (symbolId = new UUID(), bid = new UInt64(0, 0), ask = new UInt64(0, 0)) {
    this.SymbolId = symbolId
    this.Bid = bid
    this.Ask = ask
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarkupValue}
   * @param {!MarkupValue} other Other struct
   * @returns {!MarkupValue} This struct
   */
  copy (other) {
    if (other.SymbolId != null) {
      this.SymbolId = new UUID(other.SymbolId)
    } else {
      this.SymbolId = null
    }
    if (other.Bid != null) {
      this.Bid = UInt64.fromNumber(other.Bid)
    } else {
      this.Bid = null
    }
    if (other.Ask != null) {
      this.Ask = UInt64.fromNumber(other.Ask)
    } else {
      this.Ask = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarkupValue}
   * @returns {!MarkupValue} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarkupValueModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarkupValueModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarkupValue}
   * @param {!MarkupValue} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarkupValue)) {
      throw new TypeError('Instance of MarkupValue is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarkupValue}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolId: ((this.SymbolId != null) ? this.SymbolId.toString() : null),
      Bid: ((this.Bid != null) ? this.Bid.toNumber() : null),
      Ask: ((this.Ask != null) ? this.Ask.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarkupValue.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarkupValue} other Object value
   * @returns {!MarkupValue} Created struct
   */
  static fromObject (other) {
    return new MarkupValue().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarkupValue}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarkupValue.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarkupValue}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 22
  }
}

export { MarkupValue };

/**
 * Fast Binary Encoding MarkupValue field model
 */
class FieldModelMarkupValue extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Bid = new fbe.FieldModelUInt64(buffer, this.SymbolId.fbeOffset + this.SymbolId.fbeSize)
    this.Ask = new fbe.FieldModelUInt64(buffer, this.Bid.fbeOffset + this.Bid.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolId.fbeSize + this.Bid.fbeSize + this.Ask.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolId.fbeExtra + this.Bid.fbeExtra + this.Ask.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarkupValue.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 22
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarkupValue}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarkupValue}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.Bid.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Bid.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Bid.fbeSize

    if ((fbeCurrentSize + this.Ask.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Ask.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ask.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarkupValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarkupValue}
   * @param {!MarkupValue} fbeValue Default value, defaults is new MarkupValue()
   * @returns {!MarkupValue} MarkupValue value
   */
  get (fbeValue = new MarkupValue()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarkupValue}
   * @param {!MarkupValue} fbeValue MarkupValue value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolId.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolId = this.SymbolId.get()
    } else {
      fbeValue.SymbolId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolId.fbeSize

    if ((fbeCurrentSize + this.Bid.fbeSize) <= fbeStructSize) {
      fbeValue.Bid = this.Bid.get()
    } else {
      fbeValue.Bid = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Bid.fbeSize

    if ((fbeCurrentSize + this.Ask.fbeSize) <= fbeStructSize) {
      fbeValue.Ask = this.Ask.get()
    } else {
      fbeValue.Ask = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ask.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarkupValue}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarkupValue}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarkupValue}
   * @param {!MarkupValue} fbeValue MarkupValue value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarkupValue}
   * @param {MarkupValue} fbeValue MarkupValue value
   */
  setFields (fbeValue) {
    this.SymbolId.set(fbeValue.SymbolId)
    this.Bid.set(fbeValue.Bid)
    this.Ask.set(fbeValue.Ask)
  }
}

export { FieldModelMarkupValue };

/**
 * Fast Binary Encoding MarkupValue model
 */
class MarkupValueModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarkupValue(this.buffer, 4)
  }

  /**
   * Get the MarkupValue model
   * @this {!MarkupValueModel}
   * @returns {!FieldModelMarkupValue} model MarkupValue model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarkupValueModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarkupValueModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarkupValueModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarkupValueModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarkupValue.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarkupValueModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarkupValueModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarkupValueModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarkupValueModel}
   * @param {!MarkupValue} value MarkupValue value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarkupValueModel}
   * @param {!MarkupValue} value MarkupValue value, defaults is new MarkupValue()
   * @return {!object} Deserialized MarkupValue value and its size
   */
  deserialize (value = new MarkupValue()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarkupValue(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarkupValue(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarkupValueModel}
   * @param {!number} prev Previous MarkupValue model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarkupValueModel };
